interface FlavorDetails {
  vcpus: number
  ram: number
  disk: number
  ephemeral: number
  swap: number
  original_name: string
  extra_specs: Record<string, unknown>
}

export interface StatewiseVirtualMachineCount {
  success: number
  warning: number
  error: number
  total: number
}

interface NetworkAddress {
  version: number
  addr: string
  'OS-EXT-IPS:type': string
  'OS-EXT-IPS-MAC:mac_addr': string
}

interface Addresses {
  [networkName: string]: NetworkAddress[]
}

interface Link {
  rel: string
  href: string
}

interface SecurityGroup {
  name: string
}

interface ImageDetails {
  container_format: string
  created_at: string
  disk_format: string
  id: string
  name: string
  properties: Record<string, unknown>
  size: number
  status: string
  updated_at: string
}

interface OwnerDetails {
  default_project_id: string
  description: string
  domain_id: string
  email: string
  enabled: boolean
  id: string
  links: {
    self: string
  }
  name: string
  options: Record<string, unknown>
  password_expires_at: string | null
  username: string
}

interface TenantDetails {
  description: string
  domain_id: string
  enabled: boolean
  id: string
  is_domain: boolean
  links: {
    self: string
  }
  name: string
  options: Record<string, unknown>
  parent_id: string
  tags: string[]
}

interface VolumeDetails {
  attachment_id: string
  boot_index: number
  created_at: string
  delete_on_termination: boolean
  destination_type: string
  device_name: string
  device_type: string
  disk_bus: string
  id: string
  snapshot_id: string
  source_type: string
  updated_at: string
  volume_id: string
  volume_name?: string
  volume_size: number
  volume_type: string
  /** @deprecated use volume_name instead. remove in future. */
  name?: string
}

export interface VirtualMachine {
  key_name?: string
  id: string
  name: string
  status: string
  tenant_id: string
  user_id: string
  hostId: string
  image: string
  flavor: FlavorDetails
  created: string
  updated: string
  addresses: Addresses
  accessIPv4: string
  links: Link[]
  'OS-DCF:diskConfig': string
  progress: number
  'OS-EXT-AZ:availability_zone': string
  pinned_availability_zone: string
  'OS-SRV-USG:launched_at': string
  'OS-EXT-SRV-ATTR:host': string
  'OS-EXT-SRV-ATTR:hypervisor_hostname': string
  'OS-EXT-SRV-ATTR:reservation_id': string
  'OS-EXT-SRV-ATTR:launch_index': number
  'OS-EXT-SRV-ATTR:hostname': string
  'OS-EXT-SRV-ATTR:root_device_name': string
  'OS-EXT-STS:vm_state': string
  'OS-EXT-STS:power_state': number
  locked: boolean
  description: string
  security_groups: SecurityGroup[]
  flavorName: string
  state: string
  imageName: string
  imageDetails: ImageDetails
  ownerName: string
  metadata: Record<string, any>
  ownerDetails: OwnerDetails
  tenantName: string
  tenantDetails: TenantDetails
  securityGroupNames: string[]
  volumeDetails?: Record<string, VolumeDetails>
  volumeNames?: string[]
  volumeIds?: string[]
  hostDetails?: VmHostDetails
}

// Resmgr host summary embedded per VM in the /vmslist payload.
// The whole object is omitted by the backend when the VM's host has no resmgr
// record (i.e. the host was deleted), so `id` doubles as a "host exists" flag.
export interface VmHostDetails {
  id?: string
  info?: {
    // false when the host is offline/unreachable (the backend also returns
    // false when the underlying value is unknown/NULL)
    responding?: boolean
    hostname?: string
  }
}

export interface ServerGroup {
  name: string
  policy: string
}

export interface VMsListResponse {
  vms: VirtualMachine[]
}

export interface VirtualMachineLite {
  id: string
  name: string
}

// Duplicate VM names within a tenant are permitted by OpenStack, so name alone
// can't disambiguate two VMs in a picker/label. Appending a short id prefix
// (git-short-hash convention) keeps entries distinguishable without a full uuid.
// Lives here (not helpers.tsx) to stay free of that file's heavy transitive
// imports, since this is used by lightweight picker components.
export const formatVmNameWithId = (vm: { name?: string; id: string }): string =>
  vm?.name ? `${vm.name} (${vm.id.slice(0, 8)})` : vm.id

export interface VMsListLiteResponse {
  vms: VirtualMachineLite[]
}

export const ServerStates = {
  ACTIVE: 'active',
  BUILDING: 'building',
  STOPPED: 'stopped',
  PAUSED: 'paused',
  SUSPENDED: 'suspended',
  TOTAL: 'totalCount',
}

export interface IStatewiseServerLite {
  state: keyof typeof ServerStates | string
  servers: VMsListLiteResponse[]
}

export interface IStatewiseServerCount {
  [ServerStates.ACTIVE]: number
  [ServerStates.BUILDING]: number
  [ServerStates.STOPPED]: number
  [ServerStates.PAUSED]: number
  [ServerStates.SUSPENDED]: number
  [ServerStates.TOTAL]: number
}

export interface VirtualMachinesCountResponse {
  server_counts: IStatewiseServerCount
}

export interface IVirtualMachinesSelector extends VirtualMachine {
  flavorName: string
  flavorDisk: number
  flavorRam: number
  flavorVcpus: number
  taskState: string
  state: string
  pausedPowerState: boolean
  imageDetails: any
  imageName: string
  ownerName: string
  migrationAttemptedFrom?: string
  migrationDestination?: string
  securityGroupNames: string[]
  clusterName: string
  customLease?: any
  leasePolicy?: any
  expirationTime?: any
  leaseAction?: string
  migrationPriority?: string
  volumeIds?: string[]
  serverGroup?: ServerGroup
  attachedVmSnapshots?: any[]
  attachedVolumeSnapshots?: any[]
  migration?: any
  stretchedClusterName?: string
}
